import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  ExclamationTriangleIcon, 
  ChevronDownIcon, 
  ChevronUpIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  CalendarIcon,
  TagIcon,
  SparklesIcon,
  FireIcon,
  BellAlertIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { getTasks, getMyTasks, getPendingApprovalTasks, completeTask, approveTask, updateTask } from '../utils/api';
import { Task, TaskStatus, TaskPriority, TaskCategory, TaskUpdate } from '../types/api';
import { getErrorMessage } from '../utils/api';

const TasksPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]); // Store all tasks for stats calculation
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | TaskStatus>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | TaskPriority>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | TaskCategory>('all');
  const [viewMode, setViewMode] = useState<'my' | 'all'>('my');
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [editingDueDateId, setEditingDueDateId] = useState<string | null>(null);
  const [newDueDate, setNewDueDate] = useState('');
  const [dueDateReason, setDueDateReason] = useState('');
  const [updatingDueDate, setUpdatingDueDate] = useState(false);
  const [overdueFilter, setOverdueFilter] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';

  useEffect(() => {
    if (selectedLocation?.id) {
      loadTasks();
    }
  }, [selectedLocation?.id, statusFilter, priorityFilter, categoryFilter, viewMode, overdueFilter]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError('');

      let data: Task[];
      if (isAdmin && viewMode === 'all') {
        data = await getTasks({
          location_id: selectedLocation?.id,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          priority: priorityFilter !== 'all' ? priorityFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
        });
      } else if (isManager) {
        data = await getMyTasks(statusFilter !== 'all' ? statusFilter : undefined);
      } else {
        data = await getTasks({
          location_id: selectedLocation?.id,
          status: statusFilter !== 'all' ? statusFilter : undefined,
        });
      }

      // Store all tasks for stats calculation
      setAllTasks(data || []);

      // Apply overdue filter if active
      let filteredData = data || [];
      if (overdueFilter) {
        const today = new Date();
        const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        filteredData = filteredData.filter(t => {
          if (!t.due_date || t.status === 'approved') return false;
          const dueDate = new Date(t.due_date);
          const dueDateLocal = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
          return dueDateLocal < todayLocal;
        });
      }

      setTasks(filteredData);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load tasks'));
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (taskId: string) => {
    try {
      setCompletingId(taskId);
      await completeTask(taskId, { remarks: '' });
      await loadTasks();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to complete task'));
    } finally {
      setCompletingId(null);
    }
  };

  const handleApprove = async (taskId: string) => {
    try {
      setApprovingId(taskId);
      setError(''); // Clear any previous errors
      await approveTask(taskId);
      // Reload tasks to reflect the approval
      await loadTasks();
      // Note: Approved tasks will disappear from the list (by design)
      // This is expected behavior as approved tasks are excluded from default listing
    } catch (err: any) {
      const errorMsg = getErrorMessage(err, 'Failed to approve task');
      setError(errorMsg);
      console.error('Error approving task:', err);
      // Reload tasks to ensure UI is in sync
      await loadTasks();
    } finally {
      setApprovingId(null);
    }
  };

  const handleDueDateClick = (task: Task) => {
    setEditingDueDateId(task.id);
    setNewDueDate(task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '');
    setDueDateReason('');
  };

  const handleDueDateCancel = () => {
    setEditingDueDateId(null);
    setNewDueDate('');
    setDueDateReason('');
  };

  const handleDueDateSave = async (taskId: string) => {
    if (!newDueDate) {
      setError('Please select a due date');
      return;
    }

    try {
      setUpdatingDueDate(true);
      setError('');
      
      const updateData: TaskUpdate = {
        due_date: newDueDate,
        due_date_change_reason: dueDateReason || undefined
      };
      
      await updateTask(taskId, updateData);
      await loadTasks();
      handleDueDateCancel();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to update due date'));
    } finally {
      setUpdatingDueDate(false);
    }
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'reopened':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'reopened':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  const isOverdue = (task: Task) => {
    if (!task.due_date || task.status === 'approved') return false;
    // Normalize both dates to local midnight for accurate comparison
    const dueDate = new Date(task.due_date);
    const dueDateLocal = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return dueDateLocal < todayLocal;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRelativeTime = (dateString?: string) => {
    if (!dateString) return null;
    // Parse the date string and normalize to local midnight
    const date = new Date(dateString);
    const dateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    // Get today's date normalized to local midnight
    const now = new Date();
    const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calculate difference in days
    const diffMs = dateLocal.getTime() - todayLocal.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`;
    if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;
    return null;
  };

  // Calculate task statistics from all tasks (not filtered)
  const taskStats = useMemo(() => {
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    const stats = {
      total: allTasks.length,
      pending: allTasks.filter(t => t.status === 'pending').length,
      inProgress: allTasks.filter(t => t.status === 'in_progress').length,
      completed: allTasks.filter(t => t.status === 'completed').length,
      overdue: allTasks.filter(t => {
        if (!t.due_date || t.status === 'approved') return false;
        const dueDate = new Date(t.due_date);
        const dueDateLocal = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
        return dueDateLocal < todayLocal;
      }).length,
    };
    return stats;
  }, [allTasks]);

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-4 sm:py-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Tasks</h1>
          {!loading && tasks.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              {taskStats.total} task{taskStats.total !== 1 ? 's' : ''} total
              {taskStats.overdue > 0 && (
                <span className="ml-2 text-red-600 font-medium">
                  • {taskStats.overdue} overdue
                </span>
              )}
            </p>
          )}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Link
              to="/tasks/templates"
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all shadow-sm hover:shadow-md font-medium text-sm sm:text-base"
              title="Manage Task Templates"
            >
              <DocumentTextIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Templates</span>
              <span className="sm:hidden">Templates</span>
            </Link>
            <Link
              to="/tasks/new"
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md font-medium text-sm sm:text-base"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Create Task</span>
              <span className="sm:hidden">Create</span>
            </Link>
          </div>
        )}
      </div>

      {/* Quick Stats Cards - Mobile Friendly */}
      {!loading && allTasks.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <button
            onClick={() => {
              setStatusFilter('pending');
              setOverdueFilter(false);
              setFiltersOpen(true);
            }}
            className={`bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 sm:p-4 border transition-all hover:shadow-md cursor-pointer text-left ${
              statusFilter === 'pending' && !overdueFilter ? 'border-gray-400 ring-2 ring-gray-300' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <ClockIcon className="h-4 w-4 text-gray-600" />
              <span className="text-xs sm:text-sm text-gray-600 font-medium">Pending</span>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{taskStats.pending}</p>
          </button>
          <button
            onClick={() => {
              setStatusFilter('in_progress');
              setOverdueFilter(false);
              setFiltersOpen(true);
            }}
            className={`bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 sm:p-4 border transition-all hover:shadow-md cursor-pointer text-left ${
              statusFilter === 'in_progress' && !overdueFilter ? 'border-blue-400 ring-2 ring-blue-300' : 'border-blue-200'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <ClockIcon className="h-4 w-4 text-blue-600" />
              <span className="text-xs sm:text-sm text-blue-700 font-medium">In Progress</span>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-blue-900">{taskStats.inProgress}</p>
          </button>
          <button
            onClick={() => {
              setStatusFilter('completed');
              setOverdueFilter(false);
              setFiltersOpen(true);
            }}
            className={`bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-3 sm:p-4 border transition-all hover:shadow-md cursor-pointer text-left ${
              statusFilter === 'completed' && !overdueFilter ? 'border-yellow-400 ring-2 ring-yellow-300' : 'border-yellow-200'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckCircleIcon className="h-4 w-4 text-yellow-600" />
              <span className="text-xs sm:text-sm text-yellow-700 font-medium">Completed</span>
            </div>
            <p className="text-xl sm:text-2xl font-bold text-yellow-900">{taskStats.completed}</p>
          </button>
          {taskStats.overdue > 0 && (
            <button
              onClick={() => {
                setStatusFilter('all');
                setOverdueFilter(true);
                setFiltersOpen(true);
              }}
              className={`bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-3 sm:p-4 border transition-all hover:shadow-md cursor-pointer text-left ${
                overdueFilter ? 'border-red-400 ring-2 ring-red-300' : 'border-red-200'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <FireIcon className="h-4 w-4 text-red-600" />
                <span className="text-xs sm:text-sm text-red-700 font-medium">Overdue</span>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-red-900">{taskStats.overdue}</p>
            </button>
          )}
        </div>
      )}

      {/* Filters - Collapsible */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="w-full px-4 sm:px-6 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <TagIcon className="h-5 w-5 text-gray-500" />
            <span className="font-semibold text-gray-900">Filters</span>
          </div>
          {filtersOpen ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500" />
          )}
        </button>
        
        {filtersOpen && (
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4 sm:space-y-5 border-t border-gray-200 bg-gray-50/50">
            {isAdmin && (
              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => setViewMode('my')}
                  className={`flex-1 px-4 py-2.5 rounded-lg transition-all font-medium text-sm ${
                    viewMode === 'my'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  My Tasks
                </button>
                <button
                  onClick={() => setViewMode('all')}
                  className={`flex-1 px-4 py-2.5 rounded-lg transition-all font-medium text-sm ${
                    viewMode === 'all'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  All Tasks
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | TaskStatus)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="approved">Approved</option>
                  <option value="reopened">Reopened</option>
                </select>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value as 'all' | TaskPriority)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                >
                  <option value="all">All Priorities</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {/* Category Filter */}
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value as 'all' | TaskCategory)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                >
                  <option value="all">All Categories</option>
                  <option value="health">Health</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="data_entry">Data Entry</option>
                  <option value="breeding">Breeding</option>
                  <option value="harvest">Harvest</option>
                  <option value="general">General</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            <p className="text-red-800 text-sm font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="p-12 text-center">
          <LoadingSpinner size="md" />
          <p className="text-gray-500 mt-3 font-medium">Loading tasks...</p>
        </div>
      )}

      {/* Tasks List */}
      {!loading && (
        <div className="space-y-3 sm:space-y-4">
          {tasks.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ClockIcon className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No tasks found</h3>
                <p className="text-gray-600 mb-6">Get started by creating your first task</p>
                {isAdmin && (
                  <Link
                    to="/tasks/new"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm hover:shadow-md"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Create Task
                  </Link>
                )}
              </div>
            </div>
          ) : (
            tasks.map((task) => {
              const relativeTime = getRelativeTime(task.due_date);
              const overdue = isOverdue(task);
              
              return (
                <div
                  key={task.id}
                  className={`bg-white rounded-xl shadow-sm border-2 transition-all hover:shadow-md ${
                    overdue ? 'border-red-300 bg-red-50/30' : 'border-gray-200'
                  } ${task.status === 'completed' ? 'bg-yellow-50/50' : ''} ${
                    task.status === 'approved' ? 'bg-green-50/30' : ''
                  }`}
                >
                  <div className="p-4 sm:p-5">
                    {/* Header Row */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 mb-2">
                          <h3 className="font-bold text-gray-900 text-base sm:text-lg flex-1">
                            {task.title}
                          </h3>
                          {task.assignment_type === 'system' && (
                            <span className="flex-shrink-0 px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-100 to-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1">
                              <SparklesIcon className="h-3 w-3" />
                              SYSTEM
                            </span>
                          )}
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{task.description}</p>
                        )}
                      </div>
                      
                      {/* Badges - Mobile optimized */}
                      <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:flex-col sm:items-end">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(task.priority)}`}>
                          {task.priority.toUpperCase()}
                        </span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${getStatusColor(task.status)}`}>
                          {getStatusIcon(task.status)}
                          <span>{task.status.replace('_', ' ').toUpperCase()}</span>
                        </span>
                      </div>
                    </div>

                    {/* Info Grid - Mobile Friendly */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div className="flex items-center gap-2 text-sm">
                        <TagIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-600">Category:</span>
                        <span className="font-semibold text-gray-900 capitalize">
                          {task.category.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <UserIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-600">Assigned:</span>
                        <span className="font-semibold text-gray-900">
                          {task.assigned_to_manager_name || 'Unassigned'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <CalendarIcon className={`h-4 w-4 flex-shrink-0 ${overdue ? 'text-red-500' : 'text-gray-400'}`} />
                        <span className="text-gray-600">Due:</span>
                        {editingDueDateId === task.id ? (
                          <div className="flex-1 flex flex-col sm:flex-row gap-2">
                            <input
                              type="date"
                              value={newDueDate}
                              onChange={(e) => setNewDueDate(e.target.value)}
                              className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              disabled={updatingDueDate}
                            />
                            <input
                              type="text"
                              value={dueDateReason}
                              onChange={(e) => setDueDateReason(e.target.value)}
                              placeholder="Reason (optional)"
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              disabled={updatingDueDate}
                            />
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleDueDateSave(task.id)}
                                disabled={updatingDueDate || !newDueDate}
                                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleDueDateCancel}
                                disabled={updatingDueDate}
                                className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleDueDateClick(task)}
                            disabled={!isManager && !isAdmin || (isManager && task.assigned_to_manager_id !== user?.id)}
                            className={`text-left ${
                              !task.due_date 
                                ? 'text-gray-400 italic hover:text-gray-600' 
                                : `font-semibold ${overdue ? 'text-red-600' : 'text-gray-900'} hover:underline`
                            } ${
                              ((isManager && task.assigned_to_manager_id === user?.id) || isAdmin) ? 'cursor-pointer' : 'cursor-default'
                            }`}
                            title={((isManager && task.assigned_to_manager_id === user?.id) || isAdmin) ? 'Click to change due date' : ''}
                          >
                            {task.due_date ? (
                              <>
                                {formatDate(task.due_date)}
                                {relativeTime && (
                                  <span className="ml-1 text-xs font-normal text-gray-500">
                                    ({relativeTime})
                                  </span>
                                )}
                                {overdue && (
                                  <span className="ml-1.5 inline-flex items-center gap-1 text-xs font-semibold text-red-600">
                                    <FireIcon className="h-3 w-3" />
                                    Overdue
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-400 italic">
                                add due date
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                      
                      {task.comments.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600">Comments:</span>
                          <span className="font-semibold text-gray-900">
                            {task.comments.length}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Remarks and Feedback */}
                    {task.manager_remarks && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-3 border-blue-400">
                        <div className="flex items-start gap-2">
                          <UserIcon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide">Manager Remarks</span>
                            <p className="text-sm text-blue-800 mt-1">{task.manager_remarks}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {task.admin_feedback && (
                      <div className="mt-3 p-3 bg-orange-50 rounded-lg border-l-3 border-orange-400">
                        <div className="flex items-start gap-2">
                          <BellAlertIcon className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <span className="text-xs font-semibold text-orange-900 uppercase tracking-wide">Admin Feedback</span>
                            <p className="text-sm text-orange-800 mt-1">{task.admin_feedback}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons - Mobile Optimized */}
                    <div className="flex flex-col sm:flex-row gap-2 mt-4 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        className="flex-1 sm:flex-initial px-4 py-2.5 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                      >
                        <span>View Details</span>
                      </button>
                      {(isManager || isAdmin) && (task.status === 'pending' || task.status === 'reopened' || task.status === 'in_progress') && (
                        <button
                          onClick={() => handleComplete(task.id)}
                          disabled={completingId === task.id}
                          className="flex-1 sm:flex-initial px-4 py-2.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          {completingId === task.id ? 'Completing...' : 'Complete'}
                        </button>
                      )}
                      {isAdmin && task.status === 'completed' && (
                        <button
                          onClick={() => handleApprove(task.id)}
                          disabled={approvingId === task.id}
                          className="flex-1 sm:flex-initial px-4 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
                        >
                          <CheckCircleIconSolid className="h-4 w-4" />
                          {approvingId === task.id ? 'Approving...' : 'Approve'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default TasksPage;


